import { useState, useEffect, useCallback } from 'react'
import { ApiGateway } from '../config/config'
import axios from 'axios'
import { getToken } from './auth'
import { getSiteConfig } from '../community/siteProvider'
import { ROLES } from './constants'
export { ROLES } from './constants'

class RoleService {
  static #instanceRequest = false
  static #roleServiceInstance = undefined
  #queued = false

  constructor() {
    if (!RoleService.#instanceRequest) {
      throw new TypeError('Cannot be constructed. Please use RoleService.getInstance()')
    }
    RoleService.#instanceRequest = false
  }

  static getInstance() {
    if (! RoleService.#roleServiceInstance) {
      RoleService.#instanceRequest = true
      RoleService.#roleServiceInstance = new RoleService()
    }
    return RoleService.#roleServiceInstance
  }

  #getMaxPrivelege(roles) {
    const maxPrivelege = roles.reduce((p,c) => {
      if (!p || p.id > c.id) {
        return c
      }
      return p
    })
    return maxPrivelege
  }

  async getUserRole(msalInstance) {
    if (this.#queued) {
      return
    }
    this.#queued = true

    const token = await getToken()
    const koId = token?.idTokenClaims?.koId
    if (! koId) return undefined

    const basePath = `${ApiGateway.profile}/${koId}`
    const options = {
      headers: {        
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token.idToken}`
      }
    }
    const response = await axios.get(basePath, options)
    let _role = undefined
    if (response.data) {
      _role = this.#getMaxPrivelege(response.data.roles)
    }
    this.#queued = false
    return _role
  }
}



export const getRoleById = (id) => {
  return Object.values(ROLES).find(r => r.id === id)
}

function getStorageKeyName() {
  const ds = new Date().toLocaleDateString()
  return btoa(ds)
}
function getRoleEncoded(rob) {
  const enc = btoa(JSON.stringify(rob))
  return enc
}

function getStoredRole() {
  const encodedRole = window.sessionStorage.getItem(getStorageKeyName())
  if (encodedRole == null) {
    return null
  }
  const dec = atob(encodedRole)
  const rob = JSON.parse(dec)
  return rob
}
function serializeRole(role_ob) {
  window.sessionStorage.setItem(getStorageKeyName(),getRoleEncoded(role_ob))
}

export function cleanup() {
  window.sessionStorage.removeItem(getStorageKeyName())
}

export function useRole() {
  // const { instance } = useMsal()
  const [role, setRole] = useState()
  
  const [impersonatedRole, setImpersonatedRole] = useState()
  const instance = undefined

  const getAccountRole = useCallback(async () => {
    if (role) return role

    const storedRole = getStoredRole()
    if (storedRole != null) {
      setRole(storedRole)
      return storedRole
    }

    const _role = await RoleService.getInstance().getUserRole(instance)
    if (_role) {
      setRole(_role)
      serializeRole(_role)
      return _role
    }

    setRole(undefined)
    return undefined
  }, [instance, role])

  const hasRole = (roles, allowImpersonation = true) => {
    let _roles
    if (Array.isArray(roles)) {
      _roles = roles
    } else if (typeof roles === 'object') {
      _roles = [roles]
    }
    const roleCheck = (allowImpersonation && impersonatedRole) || role
    return roleCheck && _roles.some((r) => r.id === roleCheck.id)
  }

  const hasAnyRole = () => {
    const roleCheck = impersonatedRole || role
    return roleCheck && Object.values(ROLES).some((r) => r.id === roleCheck.id)
  }

  const cleanupRole = () => {
    cleanup()
  }

  const isAdminUserDespiteImpersonation = () => {
    return hasRole([ROLES.ADMIN, ROLES.MFI_ADMIN], false)
  }
  const isAdminUser = () => {
    return hasRole([ROLES.ADMIN, ROLES.MFI_ADMIN])
  }
  const isQualityManager = () => {
    return hasRole([ROLES.QSE_MGR]) || isAdminUser()
  }
  const isSafetyManager = () => {
    return hasRole([ROLES.QSE_SAFETY_MANAGER]) || isAdminUser()
  }
  const isSupplyChainManager = () => {
    return hasRole([ROLES.SUPPLY_CHAIN_MGR]) || isAdminUser()
  }
  const isSustainabilityManager = () => {
    return hasRole([ROLES.QSE_SUSTAINABILITY_MGR]) || isAdminUser()
  }
  const isImManager = () => {
    return hasRole([ROLES.IM_MANAGER]) || isAdminUser()
  }
  const isQseMember = () => {
    return hasRole([ROLES.QSE_MEMBER]) || isAdminUser()
  }
  const isBottlerUser = () => {
    return hasRole([ROLES.BOTTLER, ROLES.BOTTLER_CORP])
  }
  const isSupplyChainBottlerUser = () => {
    return hasRole([ROLES.BOTTLER_SUPPLY_CHAIN])
  }
  const isInternalUser = () => {
    return isQseMember() 
    || isQualityManager() 
    || isSafetyManager() 
    || isSustainabilityManager()
    || isImManager()
    || isSupplyChainManager()
  }
  const isSiteManager = (sitePath) => {
    if (isAdminUser()) return true
    const { managerRoles } = getSiteConfig(sitePath)
    if (managerRoles !== undefined) {
      return hasRole(managerRoles)
    }
    return false
  }
  // getAccountRole()

  const impersonate = useCallback((desiredRole) => {
    if(role?.id === ROLES.ADMIN.id) {
      setImpersonatedRole(desiredRole)
    }
  }, [role])

  const theJigIsUp = () => {
    setImpersonatedRole()
  }

  useEffect(() => {
    if (!role) {
      (async () => {
        await getAccountRole()
      })()
    }
  }, [getAccountRole, role])

  return {
    role: impersonatedRole || role,
    hasRole,
    hasAnyRole,
    cleanup: cleanupRole,
    isAdminUser,
    isAdminUserDespiteImpersonation,
    isBottlerUser,
    isInternalUser,
    isQseMember,
    isQualityManager,
    isSafetyManager,
    isSupplyChainManager,
    isSupplyChainBottlerUser,
    isSustainabilityManager,
    isImManager,
    isSiteManager,
    impersonate,
    theJigIsUp
  }
}
